import { getCurrentTenantSlug, getTenantApiUrl } from '../tenantConfig';

// Base blog API functions
export async function getBlogs(params = {}) {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    
    // Build query string
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.clinic_id) queryParams.append('clinic_id', params.clinic_id);
    if (params.language) queryParams.append('language', params.language);
    if (params.page) queryParams.append('page', params.page);
    
    const url = `${baseUrl}/blogs/posts/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const res = await fetch(url);
    const data = await res.json();
    
    return data;
  } catch (error) {
    console.error('Error fetching blogs:', error);
    return { results: [], count: 0 };
  }
}

export async function getBlogById(blogId) {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    const res = await fetch(`${baseUrl}/blogs/posts/${blogId}/`);
    const data = await res.json();
    return data;
  } catch (error) {
    console.error('Error fetching blog:', error);
    return null;
  }
}

export async function searchBlogs(query, filters = {}) {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    
    const queryParams = new URLSearchParams();
    if (query) queryParams.append('q', query);
    if (filters.city) queryParams.append('city', filters.city);
    if (filters.clinic_id) queryParams.append('clinic_id', filters.clinic_id);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.page) queryParams.append('page', filters.page);
    
    const url = `${baseUrl}/blogs/search/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const res = await fetch(url);
    const data = await res.json();
    
    return data;
  } catch (error) {
    console.error('Error searching blogs:', error);
    return { results: [], count: 0 };
  }
}

// Blog generation functions
export async function generateBlog(blogData) {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    
    const res = await fetch(`${baseUrl}/blogs/generate/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify(blogData),
    });
    
    const data = await res.json();
    return data;
  } catch (error) {
    console.error('Error generating blog:', error);
    throw error;
  }
}

export async function publishBlog(blogId) {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    
    const res = await fetch(`${baseUrl}/blogs/posts/${blogId}/publish/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    const data = await res.json();
    return data;
  } catch (error) {
    console.error('Error publishing blog:', error);
    throw error;
  }
}

export async function archiveBlog(blogId) {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    
    const res = await fetch(`${baseUrl}/blogs/posts/${blogId}/archive/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    const data = await res.json();
    return data;
  } catch (error) {
    console.error('Error archiving blog:', error);
    throw error;
  }
}

export async function updateBlog(blogId, blogData) {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    
    const res = await fetch(`${baseUrl}/blogs/posts/${blogId}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify(blogData),
    });
    
    const data = await res.json();
    return data;
  } catch (error) {
    console.error('Error updating blog:', error);
    throw error;
  }
}

export async function deleteBlog(blogId) {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    
    const res = await fetch(`${baseUrl}/blogs/posts/${blogId}/`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    return res.ok;
  } catch (error) {
    console.error('Error deleting blog:', error);
    throw error;
  }
}

// Knowledge base functions
export async function getKnowledgeBase(params = {}) {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.q) queryParams.append('q', params.q);
    
    const url = `${baseUrl}/blogs/knowledge/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const res = await fetch(url);
    const data = await res.json();
    
    return data;
  } catch (error) {
    console.error('Error fetching knowledge base:', error);
    return [];
  }
}

export async function getKnowledgeCategories() {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    const res = await fetch(`${baseUrl}/blogs/knowledge/categories/`);
    const data = await res.json();
    return data;
  } catch (error) {
    console.error('Error fetching knowledge categories:', error);
    return [];
  }
}

// SEO keywords functions
export async function getSEOKeywords() {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    const res = await fetch(`${baseUrl}/blogs/keywords/`);
    const data = await res.json();
    return data;
  } catch (error) {
    console.error('Error fetching SEO keywords:', error);
    return [];
  }
}

export async function getTrendingKeywords() {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    const res = await fetch(`${baseUrl}/blogs/keywords/trending/`);
    const data = await res.json();
    return data;
  } catch (error) {
    console.error('Error fetching trending keywords:', error);
    return [];
  }
}

// Blog automation functions
export async function triggerWeeklyGeneration() {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    
    const res = await fetch(`${baseUrl}/blogs/automation/weekly/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    const data = await res.json();
    return data;
  } catch (error) {
    console.error('Error triggering weekly generation:', error);
    throw error;
  }
}

export async function generateForClinic(clinicId) {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    
    const res = await fetch(`${baseUrl}/blogs/automation/clinic/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify({ clinic_id: clinicId }),
    });
    
    const data = await res.json();
    return data;
  } catch (error) {
    console.error('Error generating for clinic:', error);
    throw error;
  }
}

// Blog generation jobs
export async function getBlogGenerationJobs() {
  try {
    const tenant = getCurrentTenantSlug();
    const baseUrl = getTenantApiUrl(tenant);
    const res = await fetch(`${baseUrl}/blogs/jobs/`);
    const data = await res.json();
    return data;
  } catch (error) {
    console.error('Error fetching blog generation jobs:', error);
    return [];
  }
}

// Utility functions
export function formatBlogDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function truncateText(text, maxLength = 150) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

export function generateSlug(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim('-');
}
